// Условия «Справочника» в адресе страницы (005: FR-009; договорённость дорожек W1 и W2): имена —
// как у запроса directory.records.list (RecordsQuery), плюс record — открытая карточка справа,
// pair — открытое окно сравнения, group — группировка, view=questions — экран «Вопросы» (T046).
// «Главная», «Контроль», ⌘K и итог разбора ведут сюда прямо: /directory?kind=person&record=<id>,
// /directory?addressee=missing, /directory?withPairs=true, /directory?pair=<id>,
// /directory?newSinceUpload=true, /directory?view=questions. Значения по умолчанию (kind=person,
// sort=name, группировка по виду записи) в адрес не пишутся. 005-2: doubt=kind — на экране
// «Вопросы» только «Похоже на другой вид» (плитка «К разбору» на «Главной», FR-018).
// Записей «вид сменён» в списках нет (FR-009): такого условия «Состояние» тоже нет.
// 006 (T010): unit — открытая карточка подразделения справа: /directory?unit=<id>; карточка записи
// и карточка подразделения не открыты вместе — одна панель справа.
// 037 (FR-003, FR-004; research, Р3; ORG-03): node — выбранный узел панели «Структура» на вкладке
// «Сотрудники»: /directory?node=unit:<id>. Узел — не фильтр: чипа нет, «Сбросить всё» его
// оставляет; при переходе на «Организации» и «Вопросы» узел снимается. Группировка
// «подразделение» (FR-010) — только у сотрудников.
import {
  DIRECTORY_SEARCH_MAX,
  addresseeFilters,
  emailFilters,
  organizationSides,
  parseStaffNode,
  recordSortFields,
  recordStates,
} from '@kp/contracts';
import type { OrganizationSide, RecordKind, RecordState, RouteQuery } from '@kp/contracts';

export type RecordSortField = (typeof recordSortFields)[number];
export type RecordSort = RecordSortField | `-${RecordSortField}`;
export type AddresseeFilter = (typeof addresseeFilters)[number];
export type EmailFilter = (typeof emailFilters)[number];

/**
 * Группировка строк: людей — по организации основного места или вложенными группами по
 * подразделениям (037, FR-010), организаций — своя или внешняя.
 */
export type DirectoryGroup = 'organization' | 'unit' | 'side' | 'none';

const directoryGroups = ['organization', 'unit', 'side', 'none'] as const;

/** Экран рядом с вкладками «Люди / Организации»: «Вопросы» по упоминаниям (T046). */
export type DirectoryView = 'questions';

/** Фильтр «Вопросов»: только «Похоже на другой вид» (005-2, FR-018). */
export type QuestionsFilter = 'kind';

/** Состояния условия «Состояние»: «вид сменён» в списках не участвует (005-2, FR-009). */
export const listedStates: readonly RecordState[] = recordStates.filter(
  (state) => state !== 'rekinded',
);

export interface DirectorySearch {
  /** Экран «Вопросы»; нет — список записей вида kind. */
  view?: DirectoryView;
  /** На экране «Вопросы» — только вопросы о виде записи. */
  doubt?: QuestionsFilter;
  kind?: RecordKind;
  q?: string;
  /** Состояния через запятую: draft,confirmed. Нет — без архива и склеенных. */
  state?: string;
  side?: OrganizationSide;
  addressee?: AddresseeFilter;
  email?: EmailFilter;
  openItems?: boolean;
  noPrimaryCurator?: boolean;
  organizationId?: string;
  documentId?: string;
  newSinceUpload?: boolean;
  withPairs?: boolean;
  /** «Вид — решение ИИ» (005-3, FR-012): только у организаций — ИИ сам задаёт лишь этот вид. */
  kindByAi?: boolean;
  sort?: RecordSort;
  group?: DirectoryGroup;
  /** Открытая карточка записи справа. */
  record?: string;
  /** Открытое окно сравнения пары «тот же?». */
  pair?: string;
  /** Открытая карточка подразделения (006, T010). */
  unit?: string;
  /**
   * Узел панели «Структура» (037): `organization:<id>`, `unit:<id>`, `external`, `none`; нет —
   * «Все сотрудники». Только у сотрудников.
   */
  node?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const sortValues = recordSortFields.flatMap((field) => [field, `-${field}`]) as RecordSort[];

function oneOf<T extends string>(values: readonly T[], value: unknown): T | undefined {
  return typeof value === 'string' && (values as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/** Флажок из адреса: роутер читает «true» как да; строка «true» — тоже да. */
function flag(value: unknown): true | undefined {
  return value === true || value === 'true' ? true : undefined;
}

function uuid(value: unknown): string | undefined {
  return typeof value === 'string' && UUID.test(value) ? value : undefined;
}

/** Узел структуры из адреса в одном написании: `unit:<id>` строчными; не узел — нет. */
export function staffNode(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const node = parseStaffNode(value.trim());
  if (!node) return undefined;
  return 'id' in node ? `${node.kind}:${node.id}` : node.kind;
}

/** Состояния из адреса: известные, без повторов, в порядке каталога. */
export function parseStates(value: unknown): RecordState[] {
  if (typeof value !== 'string') return [];
  const wanted = new Set(value.split(',').map((part) => part.trim()));
  return listedStates.filter((state) => wanted.has(state));
}

/** Группировка по умолчанию: людей — по организации, организаций — своя или внешняя. */
export function defaultGroup(kind: RecordKind): DirectoryGroup {
  return kind === 'person' ? 'organization' : 'side';
}

export function groupOf(search: DirectorySearch): DirectoryGroup {
  const kind = search.kind ?? 'person';
  const group = search.group ?? defaultGroup(kind);
  // Группа другого вида записей не применяется: у организаций нет «организации», у людей — стороны.
  if ((group === 'organization' || group === 'unit') && kind !== 'person') return 'none';
  if (group === 'side' && kind !== 'organization') return 'none';
  return group;
}

export function parseDirectorySearch(search: Record<string, unknown>): DirectorySearch {
  const result: DirectorySearch = {};
  if (search.view === 'questions') result.view = 'questions';
  if (result.view && search.doubt === 'kind') result.doubt = 'kind';
  const kind = oneOf(['person', 'organization'] as const, search.kind);
  if (kind) result.kind = kind;
  // Число в адресе (?q=28) роутер читает как число: поиск — строка.
  const q = typeof search.q === 'number' ? String(search.q) : search.q;
  if (typeof q === 'string' && q.trim() !== '' && q.trim().length <= DIRECTORY_SEARCH_MAX) {
    result.q = q;
  }
  const states = parseStates(search.state);
  if (states.length > 0) result.state = states.join(',');
  const side = oneOf(organizationSides, search.side);
  if (side) result.side = side;
  const addressee = oneOf(addresseeFilters, search.addressee);
  if (addressee) result.addressee = addressee;
  const email = oneOf(emailFilters, search.email);
  if (email) result.email = email;
  if (flag(search.openItems)) result.openItems = true;
  if (flag(search.noPrimaryCurator)) result.noPrimaryCurator = true;
  const organizationId = uuid(search.organizationId);
  if (organizationId) result.organizationId = organizationId;
  const documentId = uuid(search.documentId);
  if (documentId) result.documentId = documentId;
  if (flag(search.newSinceUpload)) result.newSinceUpload = true;
  if (flag(search.withPairs)) result.withPairs = true;
  if (flag(search.kindByAi)) result.kindByAi = true;
  const sort = oneOf(sortValues, search.sort);
  if (sort) result.sort = sort;
  const group = oneOf(directoryGroups, search.group);
  if (group) result.group = group;
  const record = uuid(search.record);
  if (record) result.record = record;
  const pair = uuid(search.pair);
  if (pair) result.pair = pair;
  const unit = uuid(search.unit);
  if (unit) result.unit = unit;
  const node = staffNode(search.node);
  if (node && result.kind !== 'organization') result.node = node;
  return result;
}

/**
 * Условия для адреса: без пустых значений и значений по умолчанию. Условия другого вида записей
 * не пишутся: «Почта» и «Организация» — только у людей, «Своя или внешняя» и «Без основного
 * куратора» — только у организаций.
 */
export function normalizeDirectorySearch(search: DirectorySearch): DirectorySearch {
  const kind = search.kind ?? 'person';
  const result: DirectorySearch = {};
  if (search.view === 'questions') result.view = 'questions';
  if (result.view && search.doubt) result.doubt = search.doubt;
  if (kind === 'organization') result.kind = 'organization';
  if (search.q && search.q.trim() !== '') result.q = search.q;
  if (search.state) result.state = search.state;
  if (kind === 'organization' && search.side) result.side = search.side;
  if (search.addressee) result.addressee = search.addressee;
  if (kind === 'person' && search.email) result.email = search.email;
  if (search.openItems) result.openItems = true;
  if (kind === 'organization' && search.noPrimaryCurator) result.noPrimaryCurator = true;
  if (kind === 'person' && search.organizationId) result.organizationId = search.organizationId;
  if (search.documentId) result.documentId = search.documentId;
  if (search.newSinceUpload) result.newSinceUpload = true;
  if (search.withPairs) result.withPairs = true;
  if (kind === 'organization' && search.kindByAi) result.kindByAi = true;
  if (search.sort && search.sort !== 'name') result.sort = search.sort;
  if (search.group && search.group !== defaultGroup(kind)) result.group = search.group;
  if (search.record) result.record = search.record;
  if (search.pair) result.pair = search.pair;
  if (search.unit) result.unit = search.unit;
  if (kind === 'person' && search.node) result.node = search.node;
  return result;
}

/** Сколько условий сужает список: число на кнопке «Фильтр · N». Поиск — отдельно. */
export function filterCount(search: DirectorySearch): number {
  const n = normalizeDirectorySearch(search);
  return [
    n.state,
    n.side,
    n.addressee,
    n.email,
    n.openItems,
    n.noPrimaryCurator,
    n.organizationId,
    n.documentId,
    n.newSinceUpload,
    n.withPairs,
    n.kindByAi,
  ].filter((value) => value !== undefined).length;
}

/** Поиск и фильтры сужают список: «Ничего не найдено» вместо «Сотрудников пока нет». */
export function hasDirectoryFilters(search: DirectorySearch): boolean {
  return Boolean(search.q && search.q.trim() !== '') || filterCount(search) > 0;
}

/**
 * «Сбросить всё»: остаются вид записей, сортировка, группировка, открытая карточка и узел
 * структуры (037): узел — не условие фильтра, а место в дереве.
 */
export function withoutFilters(search: DirectorySearch): DirectorySearch {
  return normalizeDirectorySearch({
    kind: search.kind,
    sort: search.sort,
    group: search.group,
    record: search.record,
    unit: search.unit,
    node: search.node,
  });
}

/**
 * «Вопросы»: только экран; открытая карточка записи остаётся. 031 (FR-013; ASG-RES-03): фильтр по
 * протоколу (documentId) остаётся — «Разобрать» окна готовности и окна перевода ведёт на вопросы
 * одного протокола: /directory?view=questions&documentId=….
 */
export function questionsView(search: DirectorySearch): DirectorySearch {
  return normalizeDirectorySearch({
    view: 'questions',
    kind: search.kind,
    documentId: search.documentId,
    record: search.record,
    unit: search.unit,
  });
}

/** Запрос «Вопросов» по упоминаниям: ждут решения; с протоколом — только его (031, FR-013). */
export function questionsQuery(
  documentId: string | undefined,
  limit: number,
): RouteQuery<'directory.mentions.list'> {
  return { pending: 'true', limit, ...(documentId ? { documentId } : {}) };
}

/**
 * Смена вкладки: поиск и общие условия остаются, условия другого вида, карточка и узел структуры
 * (037) — нет.
 */
export function switchKind(search: DirectorySearch, kind: RecordKind): DirectorySearch {
  return normalizeDirectorySearch({
    kind,
    q: search.q,
    state: search.state,
    addressee: search.addressee,
    openItems: search.openItems,
    documentId: search.documentId,
    newSinceUpload: search.newSinceUpload,
    withPairs: search.withPairs,
    sort: search.sort,
  });
}

/** Запрос к серверу — те же условия, что в адресе. */
export function recordsQuery(search: DirectorySearch): RouteQuery<'directory.records.list'> {
  const n = normalizeDirectorySearch(search);
  const q = n.q?.trim();
  return {
    kind: search.kind ?? 'person',
    sort: n.sort ?? 'name',
    ...(q ? { q } : {}),
    ...(n.state ? { state: n.state } : {}),
    ...(n.side ? { side: n.side } : {}),
    ...(n.addressee ? { addressee: n.addressee } : {}),
    ...(n.email ? { email: n.email } : {}),
    ...(n.openItems ? { openItems: 'true' } : {}),
    ...(n.noPrimaryCurator ? { noPrimaryCurator: 'true' } : {}),
    ...(n.organizationId ? { organizationId: n.organizationId } : {}),
    ...(n.node ? { node: n.node } : {}),
    ...(n.documentId ? { documentId: n.documentId } : {}),
    ...(n.newSinceUpload ? { newSinceUpload: 'true' } : {}),
    ...(n.withPairs ? { withPairs: 'true' } : {}),
    ...(n.kindByAi ? { kindByAi: 'true' } : {}),
  };
}
